import React from 'react';
import { useFormikContext } from 'formik';

import MultiSelectMenu from '@dashboard/components/MultiSelectMenu';
import { Label } from '@dashboard/components/Form';
import { Select } from '@dashboard/components/Formik';
import { getRuleLabels } from '@dashboard/scenes/PageEdit/scenes/PageProduct/components/OrderBumps/describeBumpRule';

export const NO_TICKET_TYPE_RULE = {
  enabled: false,
  comparison: 'IS',
  ticketTypeIds: [],
  labels: [],
};

export const toTicketTypeRuleFormValues = logic => ({
  enabled: logic?.enabled === true,
  comparison: logic?.comparison === 'IS_NOT' ? 'IS_NOT' : 'IS',
  ticketTypeIds: (logic?.ticketTypeIds ?? []).map(String),
  labels: logic?.labels ?? [],
});

// Names are saved with the rule so the badge can still name a source that was
// deleted later; a source no longer on the page keeps the name saved before.
export const prepareTicketTypeRule = (logic, allTicketTypes) => {
  const rule = toTicketTypeRuleFormValues(logic);
  return {
    ...rule,
    labels: getRuleLabels({ ...rule, subject: 'TICKET' }, rule.labels, {
      ticketTypes: allTicketTypes ?? [],
    }),
  };
};

// The group name tells two same-named tickets in different groups apart.
export const getRuleSourceOptions = (ticketGroups, ticketTypeId) =>
  (ticketGroups ?? []).flatMap(group =>
    (group.ticketTypes ?? [])
      .filter(ticketType => !ticketTypeId || String(ticketType._id) !== String(ticketTypeId))
      .map(ticketType => ({
        id: String(ticketType._id),
        label: ticketType.name,
        detail: group.name,
      })),
  );

export default function TicketTypeConditionalLogicField({ ticketGroups, ticketTypeId }) {
  const { values, setFieldValue } = useFormikContext();

  const logic = values.showHideLogic ?? NO_TICKET_TYPE_RULE;
  const selectedIds = (logic.ticketTypeIds ?? []).map(String);
  const items = getRuleSourceOptions(ticketGroups, ticketTypeId);

  const update = patch =>
    setFieldValue('showHideLogic', {
      ...logic,
      enabled: true,
      comparison: logic.comparison || 'IS',
      ticketTypeIds: selectedIds,
      ...patch,
    });

  const toggle = id =>
    update({
      ticketTypeIds: selectedIds.includes(id)
        ? selectedIds.filter(candidate => candidate !== id)
        : [...selectedIds, id],
    });

  return (
    <div className="grid grid-cols-[4rem_1fr] items-center gap-x-2 gap-y-2">
      <Label isInline htmlFor="showHideLogic.comparison">
        Ticket
      </Label>
      <Select
        id="showHideLogic.comparison"
        name="showHideLogic.comparison"
        className="w-32"
        value={logic.comparison || 'IS'}
        onChange={event => update({ comparison: event.target.value })}
      >
        <option value="IS">is</option>
        <option value="IS_NOT">is not</option>
      </Select>

      <div />
      <MultiSelectMenu
        id="showHideLogic.ticketTypeIds"
        placeholder="Select tickets"
        emptyText="This event has no other tickets yet."
        items={items}
        selectedIds={selectedIds}
        onToggle={toggle}
      />
    </div>
  );
}
